import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import {
  PaymentsAndBillingInfoType,
  CalculatePaymentPlanResponse,
  CalculatePaymentPlanRequest,
} from '@clearcover/core-app-types';
import {
  FormInput,
  FormModalHeader,
  FormModalContent,
} from 'Shared/components';

import { FlexGrid, FlexGridColumn } from '@clearcover/atlas';
import { useLazyApi } from 'Shared/services/Api';
import { usePolicyParams } from 'Policies/utils';
import styles from './styles.module.css';
import {
  dayOfWeekOptions,
  generateSemiMonthlyOptions,
  generateMonthlyOptions,
} from './utils';
import { CustomFormModalFooter } from './CustomFormModalFooter';
import { PaymentScheduleList } from './PaymentScheduleList';
import { useToast } from 'Shared/components';

const FORM_VALIDATION = Yup.object({
  installmentInterval: Yup.string().required('Payment frequency is required'),
  daysOfWeek: Yup.string().when('installmentInterval', {
    is: (val: string) => val === 'WEEKLY' || val === 'BI_WEEKLY',
    then: (schema: Yup.StringSchema) =>
      schema.required('Day of week is required'),
    otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
  }),
  daysOfMonth: Yup.string().when('installmentInterval', {
    is: (val: string) => val === 'SEMI_MONTHLY' || val === 'MONTHLY',
    then: (schema: Yup.StringSchema) =>
      schema.required('Days of month is required'),
    otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
  }),
});

export type EditPayPlanProps = {
  billingInfo?: PaymentsAndBillingInfoType;
  isRenewalDraft: boolean;
  close: () => void;
};

export const EditPayPlan: FC<EditPayPlanProps> = ({
  billingInfo,
  isRenewalDraft,
  close,
}) => {
  const { toast } = useToast();
  const { policyId, draftId } = usePolicyParams();
  const history = useHistory();
  const [
    paymentPlanResponse,
    setPaymentPlanResponse,
  ] = useState<CalculatePaymentPlanResponse | null>(null);
  const isRenewalPlanChange = isRenewalDraft && !!draftId;

  const [{ loading: isCalculatingPayPlan }, calculatePaymentPlan] = useLazyApi({
    url: isRenewalPlanChange
      ? `policies/renewal/${policyId}/calculatePayPlan`
      : 'policies/calculatePayPlan',
    method: 'POST',
  });

  const calculatePayPlan = useCallback(
    (
      installmentInterval: string,
      daysOfWeek?: string,
      daysOfMonth?: string
    ) => {
      const data: CalculatePaymentPlanRequest = {
        policyId,
        installmentInterval,
      };

      if (
        installmentInterval === 'WEEKLY' ||
        installmentInterval === 'BI_WEEKLY'
      ) {
        data.daysOfWeek = [daysOfWeek];
      } else if (installmentInterval === 'SEMI_MONTHLY') {
        data.daysOfMonth = daysOfMonth?.split(',').map(Number);
      } else if (installmentInterval === 'MONTHLY') {
        data.daysOfMonth = [parseInt(daysOfMonth || '1')];
      }

      if (isRenewalPlanChange) {
        data.minimumRequiredDownpayment = {
          amount: 1,
          type: 'INSTALLMENT',
        };
      }

      calculatePaymentPlan({
        data,
      })
        .then(response => {
          setPaymentPlanResponse(response.data as CalculatePaymentPlanResponse);
        })
        .catch(() => {
          toast({
            message: 'Error calculating payment plan',
            type: 'error',
            position: 'bottom',
            withSecondaryNav: false,
          });
          setPaymentPlanResponse(null);
        });
    },
    [calculatePaymentPlan, isRenewalPlanChange, policyId, toast]
  );

  const createHandleContinue = useCallback(
    (
      installmentInterval: CalculatePaymentPlanResponse['installmentInterval'],
      daysOfWeek?: CalculatePaymentPlanResponse['paymentsDueOn']['daysOfWeek'],
      daysOfMonth?: CalculatePaymentPlanResponse['paymentsDueOn']['daysOfMonth']
    ) => {
      return () => {
        const queryParams = new URLSearchParams({
          installmentInterval,
        });

        if (daysOfWeek) {
          queryParams.set('daysOfWeek', daysOfWeek);
        }

        if (daysOfMonth) {
          queryParams.set('daysOfMonth', daysOfMonth);
        }

        if (isRenewalPlanChange) {
          history.push(
            `/policies/${policyId}/draft/${draftId}/payPlan/summary?${queryParams.toString()}`
          );
        } else {
          history.push(
            `/policies/${policyId}/payPlan/summary?${queryParams.toString()}`
          );
        }
      };
    },
    [draftId, history, isRenewalPlanChange, policyId]
  );

  return (
    <Formik
      initialValues={{
        installmentInterval: billingInfo?.installmentInterval || '',
        daysOfWeek: billingInfo?.paymentsDueOn?.daysOfWeek[0] || '',
        daysOfMonth:
          billingInfo?.paymentsDueOn?.daysOfMonth[0]?.toString() || '',
      }}
      onSubmit={(_, { setSubmitting }) => {
        setSubmitting(false);
      }}
      validationSchema={FORM_VALIDATION}
      validateOnMount
      validateOnChange
    >
      {({
        handleBlur,
        setFieldValue,
        setFieldTouched,
        values,
        touched,
        errors,
      }) => {
        const handleInstallmentChange = (interval: string) => {
          setFieldValue('daysOfWeek', '');
          setFieldValue('daysOfMonth', '');
          setFieldTouched('daysOfWeek', false);
          setFieldTouched('daysOfMonth', false);
          setFieldValue('installmentInterval', interval, true);

          // For FULL_PAY, call API immediately since no day selection needed
          if (interval === 'FULL_PAY') {
            calculatePayPlan(interval);
          }
        };

        const handleDayChange = (field: string, value: string) => {
          setFieldValue(field, value);

          // After setting day value, check if form is complete and call API
          const updatedValues = { ...values, [field]: value };
          if (updatedValues.installmentInterval === 'FULL_PAY') return;

          if (
            ['WEEKLY', 'BI_WEEKLY'].includes(
              updatedValues.installmentInterval
            ) &&
            field === 'daysOfWeek' &&
            value
          ) {
            calculatePayPlan(updatedValues.installmentInterval, value);
          } else if (
            ['SEMI_MONTHLY', 'MONTHLY'].includes(
              updatedValues.installmentInterval
            ) &&
            field === 'daysOfMonth' &&
            value
          ) {
            calculatePayPlan(
              updatedValues.installmentInterval,
              undefined,
              value
            );
          }
        };

        const isFormIncomplete = () => {
          return Boolean(
            !!values.installmentInterval &&
              values.installmentInterval !== 'FULL_PAY' &&
              ((['WEEKLY', 'BI_WEEKLY'].includes(values.installmentInterval) &&
                (errors.daysOfWeek || !values.daysOfWeek)) ||
                (['SEMI_MONTHLY', 'MONTHLY'].includes(
                  values.installmentInterval
                ) &&
                  (errors.daysOfMonth || !values.daysOfMonth)))
          );
        };

        const shouldShowSchedule =
          !isFormIncomplete() && !isCalculatingPayPlan && paymentPlanResponse;

        return (
          <>
            <FormModalHeader>Edit Pay Plan</FormModalHeader>
            <FormModalContent>
              <FlexGrid>
                <FlexGridColumn mobile={8} mobileOffset={2}>
                  <section>
                    <h2>Choose a payment plan</h2>
                    <p>
                      Completing the customer's plan change may require a
                      payment today. You'll see the full amount before
                      submitting.
                    </p>
                    <h3>Payment Frequency</h3>
                    <FormInput
                      className={classNames(
                        styles.costRadioButton,
                        styles.top,
                        {
                          [styles.checked]:
                            values.installmentInterval === 'WEEKLY',
                        }
                      )}
                      variant={'radio'}
                      label={
                        <div className={styles.costRadioButtonLabel}>
                          <h5 className={styles.radioLabel}>Weekly</h5>
                          <div className={styles.radioSubLabel}>
                            Payments made weekly
                          </div>
                          <div className={styles.radioButtonCost}></div>
                        </div>
                      }
                      disabled={isCalculatingPayPlan}
                      checked={values.installmentInterval === 'WEEKLY'}
                      onChecked={() => {
                        handleInstallmentChange('WEEKLY');
                      }}
                      name={'installmentInterval'}
                      value={'WEEKLY'}
                    />
                    <FormInput
                      className={classNames(
                        styles.costRadioButton,
                        styles.top,
                        {
                          [styles.checked]:
                            values.installmentInterval === 'BI_WEEKLY',
                        }
                      )}
                      variant={'radio'}
                      label={
                        <div className={styles.costRadioButtonLabel}>
                          <h5 className={styles.radioLabel}>Every two weeks</h5>
                          <div className={styles.radioSubLabel}>
                            Payments made every two weeks
                          </div>
                          <div className={styles.radioButtonCost}></div>
                        </div>
                      }
                      disabled={isCalculatingPayPlan}
                      checked={values.installmentInterval === 'BI_WEEKLY'}
                      onChecked={() => {
                        handleInstallmentChange('BI_WEEKLY');
                      }}
                      name={'installmentInterval'}
                      value={'BI_WEEKLY'}
                    />
                    <FormInput
                      className={classNames(
                        styles.costRadioButton,
                        styles.top,
                        {
                          [styles.checked]:
                            values.installmentInterval === 'SEMI_MONTHLY',
                        }
                      )}
                      variant={'radio'}
                      label={
                        <div className={styles.costRadioButtonLabel}>
                          <h5 className={styles.radioLabel}>Twice a month</h5>
                          <div className={styles.radioSubLabel}>
                            Payments made twice a month
                          </div>
                          <div className={styles.radioButtonCost}></div>
                        </div>
                      }
                      disabled={isCalculatingPayPlan}
                      checked={values.installmentInterval === 'SEMI_MONTHLY'}
                      onChecked={() => {
                        handleInstallmentChange('SEMI_MONTHLY');
                      }}
                      name={'installmentInterval'}
                      value={'SEMI_MONTHLY'}
                    />
                    <FormInput
                      className={classNames(
                        styles.costRadioButton,
                        styles.top,
                        {
                          [styles.checked]:
                            values.installmentInterval === 'MONTHLY',
                        }
                      )}
                      variant={'radio'}
                      label={
                        <div className={styles.costRadioButtonLabel}>
                          <h5 className={styles.radioLabel}>
                            Monthly Payments
                          </h5>
                          <div className={styles.radioSubLabel}>
                            Payments made monthly
                          </div>
                          <div className={styles.radioButtonCost}></div>
                        </div>
                      }
                      disabled={isCalculatingPayPlan}
                      checked={values.installmentInterval === 'MONTHLY'}
                      onChecked={() => {
                        handleInstallmentChange('MONTHLY');
                      }}
                      name={'installmentInterval'}
                      value={'MONTHLY'}
                    />
                    <FormInput
                      className={classNames(
                        styles.costRadioButton,
                        styles.bottom,
                        {
                          [styles.checked]:
                            values.installmentInterval === 'FULL_PAY',
                        }
                      )}
                      variant={'radio'}
                      label={
                        <div className={styles.costRadioButtonLabel}>
                          <h5 className={styles.radioLabel}>Full pay</h5>
                          <div className={styles.radioSubLabel}>
                            Pay entire policy balance today
                          </div>
                          <div className={styles.radioButtonCost}></div>
                        </div>
                      }
                      disabled={isCalculatingPayPlan}
                      checked={values.installmentInterval === 'FULL_PAY'}
                      onChecked={() => {
                        handleInstallmentChange('FULL_PAY');
                      }}
                      name={'installmentInterval'}
                      value={'FULL_PAY'}
                    />

                    {values.installmentInterval !== 'FULL_PAY' && (
                      <div className={styles.daySelectionSection}>
                        <div className={styles.daySelectionContainer}>
                          <h3>Payments will be made on</h3>
                          <div className={styles.daySelectionDropdown}>
                            {(values.installmentInterval === 'WEEKLY' ||
                              values.installmentInterval === 'BI_WEEKLY') && (
                              <FormInput
                                key={`daysOfWeek-${values.installmentInterval}`}
                                variant="selectAutocomplete"
                                name="daysOfWeek"
                                onChange={(value: string) => {
                                  handleDayChange('daysOfWeek', value);
                                }}
                                onBlur={handleBlur}
                                value={values.daysOfWeek}
                                options={dayOfWeekOptions}
                                placeholder="Select a payment day"
                                disabled={isCalculatingPayPlan}
                                errorMessage={
                                  touched.daysOfWeek
                                    ? errors.daysOfWeek
                                    : undefined
                                }
                              />
                            )}
                            {values.installmentInterval === 'SEMI_MONTHLY' && (
                              <FormInput
                                key={`daysOfMonth-${values.installmentInterval}`}
                                variant="selectAutocomplete"
                                name="daysOfMonth"
                                onChange={(value: string) => {
                                  handleDayChange('daysOfMonth', value);
                                }}
                                onBlur={handleBlur}
                                value={values.daysOfMonth?.toString() || ''}
                                options={generateSemiMonthlyOptions()}
                                placeholder="Select a payment day"
                                disabled={isCalculatingPayPlan}
                                errorMessage={
                                  touched.daysOfMonth
                                    ? errors.daysOfMonth
                                    : undefined
                                }
                              />
                            )}
                            {values.installmentInterval === 'MONTHLY' && (
                              <FormInput
                                key={`daysOfMonth-${values.installmentInterval}`}
                                variant="selectAutocomplete"
                                name="daysOfMonth"
                                onChange={(value: string) => {
                                  handleDayChange('daysOfMonth', value);
                                }}
                                value={values.daysOfMonth?.toString() || ''}
                                onBlur={handleBlur}
                                options={generateMonthlyOptions()}
                                placeholder="Select a payment day"
                                disabled={isCalculatingPayPlan}
                                errorMessage={
                                  touched.daysOfMonth
                                    ? errors.daysOfMonth
                                    : undefined
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {shouldShowSchedule && (
                      <PaymentScheduleList paymentPlan={paymentPlanResponse} />
                    )}
                  </section>
                </FlexGridColumn>
              </FlexGrid>
            </FormModalContent>
            <CustomFormModalFooter
              paymentPlan={paymentPlanResponse}
              isRenewalPlanChange={isRenewalPlanChange}
              close={close}
              isLoading={isCalculatingPayPlan}
              isFormIncomplete={isFormIncomplete()}
              onContinue={createHandleContinue(
                values.installmentInterval,
                values.daysOfWeek,
                values.daysOfMonth?.toString()
              )}
            />
          </>
        );
      }}
    </Formik>
  );
};
